import type { TripCashFigure } from '@chukua/common';
import { Button } from 'antd';
import { formatMoney } from '../../../shared/policies/format-money';
import { canCarryBalance, canReturnCash, cashPositionSentence } from '../policies/trip-cash-wording.policy';
import type { SettleMode } from './TripCashSettleDrawer';

interface TripCashFigureRowProps {
  figure: TripCashFigure;
  pendingExpenses: number;
  canRecord: boolean;
  onSettle: (mode: SettleMode) => void;
}

function Amount({ label, minor, currency }: { label: string; minor: number; currency: TripCashFigure['currency'] }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm font-medium text-fg">{formatMoney({ amountMinor: minor, currency })}</dd>
    </div>
  );
}

/** One driver's cash for the trip: given, spent on receipts, handed back, and what is left. */
export function TripCashFigureRow({ figure, pendingExpenses, canRecord, onSettle }: TripCashFigureRowProps) {
  const { currency } = figure;
  return (
    <div className="rounded-lg border border-line px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-fg">{figure.driverName}</p>
        <p className="text-sm text-muted">{cashPositionSentence(figure)}</p>
      </div>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Amount label="Given" minor={figure.advanceMinor} currency={currency} />
        <Amount label="Spent (receipted)" minor={figure.receiptedSpendMinor} currency={currency} />
        <Amount label="Returned" minor={figure.cashReturnedMinor} currency={currency} />
        <Amount label={figure.balanceMinor < 0 ? 'Owed to driver' : 'Balance'} minor={Math.abs(figure.balanceMinor)} currency={currency} />
      </dl>
      {canRecord && (
        <div className="mt-3 flex gap-2">
          {canReturnCash(figure, pendingExpenses) && <Button onClick={() => onSettle('return')}>Record cash returned</Button>}
          {canCarryBalance(figure, pendingExpenses) && <Button onClick={() => onSettle('carry')}>Carry balance</Button>}
        </div>
      )}
    </div>
  );
}
